import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Empty, Popconfirm, Space, Table, Tag, Tooltip, message } from 'antd';
import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  QuestionCircleFilled,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import Can from '@shared/permissions/Can';
import ListPageHeader from '@shared/ListPageHeader';
import InventoryTypeReclassifyRuleConceptModal from '@shared/inventory_type_reclassify_rules/InventoryTypeReclassifyRuleConceptModal';
import {
  inventoryTypeReclassifyRuleService,
  type Rule,
  type Tier,
} from '@services/admin/inventoryTypeReclassifyRuleService';

const i18nBase = 'admin.pages.inventory_type_reclassify_rules';

const PRIORITY_STEP = 10;

const sortByPriority = (list: Rule[]): Rule[] =>
  [...list].sort((a, b) => a.priority - b.priority || a.name.localeCompare(b.name));

/**
 * Merchant-facing list of inventory auto-reclassify rules.
 *
 * 順序由清單上的 ↑/↓ 按鈕控制（取代以前的 priority input）。每次搬動後
 * 統一重新編號為 0/10/20/...，保證任何時刻 priority 都不重複。
 */
export default function AdminInventoryTypeReclassifyRulesList() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [rules, setRules] = useState<Rule[]>([]);
  const [loading, setLoading] = useState(true);
  const [reordering, setReordering] = useState(false);
  const [conceptModalVisible, setConceptModalVisible] = useState(false);

  const fetchRules = useCallback(async () => {
    try {
      const res = await inventoryTypeReclassifyRuleService.index();
      if (res.success) setRules(sortByPriority(res.data));
    } catch (err) {
      console.error('Failed to load reclassify rules', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRules();
  }, [fetchRules]);

  const handleDelete = useCallback(
    async (r: Rule) => {
      try {
        const res = await inventoryTypeReclassifyRuleService.destroy(r.id);
        if (res.success) {
          message.success(t(`${i18nBase}.list.delete_success`));
          await fetchRules();
        } else {
          message.error(t('common.error.delete_failed'));
        }
      } catch (err) {
        console.error('Failed to delete reclassify rule', err);
        message.error(t('common.error.delete_failed'));
      }
    },
    [t, fetchRules],
  );

  const move = async (index: number, direction: 'up' | 'down') => {
    const target = direction === 'up' ? index - 1 : index + 1;
    if (target < 0 || target >= rules.length) return;
    if (reordering) return;

    const reordered = [...rules];
    [reordered[index], reordered[target]] = [reordered[target], reordered[index]];

    const before = new Map(rules.map((r) => [r.id, r.priority]));
    const normalized = reordered.map((r, i) => ({ ...r, priority: i * PRIORITY_STEP }));
    const changed = normalized.filter((r) => before.get(r.id) !== r.priority);

    setRules(normalized);
    setReordering(true);
    try {
      await Promise.all(
        changed.map((r) =>
          inventoryTypeReclassifyRuleService.update(r.id, { priority: r.priority }),
        ),
      );
    } catch (err) {
      console.error('Failed to persist new priority order', err);
      message.error(t('common.error.unknown', { defaultValue: 'Failed to save' }));
      setRules(rules); // rollback
    } finally {
      setReordering(false);
    }
  };

  const formatTierBrief = (tier: Tier, next: Tier | undefined): string => {
    const from = tier.original_expiration_date_threshold;
    const range = next
      ? t(`${i18nBase}.form.condition_range_from_to`, { from, to: next.original_expiration_date_threshold - 1 })
      : t(`${i18nBase}.form.condition_range_open_ended`, { from });
    const condition =
      tier.trigger_mechanism === 'by_days'
        ? t(`${i18nBase}.list.condition_brief_by_days`, { days: tier.days_before_expiration })
        : t(`${i18nBase}.list.condition_brief_by_ratio`, { ratio: tier.expiration_ratio });
    return `${range} → ${condition}`;
  };

  const columns = useMemo(
    () => [
      {
        title: t(`${i18nBase}.list.column_priority`),
        key: 'index',
        width: 80,
        render: (_: unknown, _r: Rule, index: number) => (
          <Space size={4}>
            <Can requiredPermission="inventory_type_reclassify_rules:write">
              <Button
                size="small"
                type="text"
                icon={<ArrowUpOutlined />}
                disabled={index === 0 || reordering}
                onClick={() => move(index, 'up')}
              />
            </Can>
            <Can requiredPermission="inventory_type_reclassify_rules:write">
              <Button
                size="small"
                type="text"
                icon={<ArrowDownOutlined />}
                disabled={index === rules.length - 1 || reordering}
                onClick={() => move(index, 'down')}
              />
            </Can>
          </Space>
        ),
      },
      {
        title: t(`${i18nBase}.list.column_name`),
        dataIndex: 'name',
        key: 'name',
      },
      {
        title: t(`${i18nBase}.list.column_source`),
        key: 'source',
        render: (_: unknown, r: Rule) =>
          r.source_inventory_type?.name ?? r.source_inventory_type_id,
      },
      {
        title: t(`${i18nBase}.list.column_target`),
        key: 'target',
        render: (_: unknown, r: Rule) =>
          r.target_inventory_type?.name ?? r.target_inventory_type_id,
      },
      {
        title: t(`${i18nBase}.list.column_tiers`),
        key: 'tiers',
        render: (_: unknown, r: Rule) => {
          const sorted = [...r.tiers].sort(
            (a, b) => a.original_expiration_date_threshold - b.original_expiration_date_threshold,
          );
          return (
            <div>
              {sorted.map((tier, i) => (
                <div key={tier.id ?? i}>{formatTierBrief(tier, sorted[i + 1])}</div>
              ))}
            </div>
          );
        },
      },
      {
        title: t(`${i18nBase}.list.column_enabled`),
        key: 'enabled',
        render: (_: unknown, r: Rule) => (
          <Tag color={r.enabled ? 'success' : 'default'}>
            {r.enabled
              ? t(`${i18nBase}.list.column_enabled_on`)
              : t(`${i18nBase}.list.column_enabled_off`)}
          </Tag>
        ),
      },
      {
        title: t(`${i18nBase}.list.column_last_editor`),
        key: 'last_editor',
        render: (_: unknown, r: Rule) => r.owner_merchant_user?.name ?? '-',
      },
      {
        title: t(`${i18nBase}.list.column_actions`),
        key: 'actions',
        width: 160,
        render: (_: unknown, r: Rule) => (
          <Can requiredPermission="inventory_type_reclassify_rules:write">
            <Space size={0}>
              <Button
                type="link"
                size="small"
                icon={<EditOutlined />}
                onClick={() => navigate(`/inventory_type_reclassify_rules/${r.id}`)}
              >
                {t(`${i18nBase}.list.edit_button`)}
              </Button>
              <Popconfirm
                title={t(`${i18nBase}.list.delete_confirm_title`, { name: r.name })}
                onConfirm={() => handleDelete(r)}
                okText={t('common.button.confirm')}
                cancelText={t('common.button.cancel')}
              >
                <Button type="link" size="small" danger icon={<DeleteOutlined />}>
                  {t(`${i18nBase}.list.delete_button`)}
                </Button>
              </Popconfirm>
            </Space>
          </Can>
        ),
      },
    ],
    [t, rules.length, reordering, navigate, handleDelete],
  );

  return (
    <div>
      <ListPageHeader
        basePath="/admin"
        items={['stocks', 'stocks.inventory_type_reclassify_rules']}
        guideButton={
          <Tooltip title={t('concepts.inventory_type_reclassify_rules.tooltip')}>
            <Button
              type="text"
              size="small"
              icon={<QuestionCircleFilled style={{ color: '#1890ff' }} />}
              onClick={() => setConceptModalVisible(true)}
              style={{ padding: 0, height: 'auto', minWidth: 'auto' }}
            />
          </Tooltip>
        }
        actions={
          <Can requiredPermission="inventory_type_reclassify_rules:write">
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => navigate('/inventory_type_reclassify_rules/new')}
            >
              {t('admin.pages.inventory_type_reclassify_rules.list.create_button')}
            </Button>
          </Can>
        }
      />
      {!loading && rules.length === 0 ? (
        <Empty
          description={t('admin.pages.inventory_type_reclassify_rules.list.empty_state')}
        />
      ) : (
        <Table
          rowKey="id"
          loading={loading}
          dataSource={rules}
          columns={columns}
          pagination={false}
        />
      )}
      <InventoryTypeReclassifyRuleConceptModal
        visible={conceptModalVisible}
        onClose={() => setConceptModalVisible(false)}
      />
    </div>
  );
}
